// src/hooks/useHeartbeat.js
import { useEffect, useRef } from 'react';
import { debugLog } from '../config/featureFlags';

/**
 * Función para detectar el sistema operativo del usuario con versión específica
 * @returns {Promise<string>} Identificador del sistema operativo con versión
 */
const detectOperatingSystem = async () => {
  if (typeof navigator === 'undefined') return 'unknown';
  
  const platform = navigator.platform || '';
  const userAgent = navigator.userAgent || '';
  
  // Detección de Windows con versión específica
  if (platform.includes('Win') || userAgent.includes('Windows')) {
    const winVersion = userAgent.match(/Windows NT (\d+\.\d+)/);
    if (winVersion) {
      const version = winVersion[1];
      if (version === '10.0') {
        // Windows 10 o Windows 11 (ambos reportan 10.0 por compatibilidad)
        // Intentar detectar Windows 11 usando la API moderna userAgentData
        if (navigator.userAgentData && typeof navigator.userAgentData.getHighEntropyValues === 'function') {
          try {
            const highEntropyValues = await navigator.userAgentData.getHighEntropyValues(['platformVersion']);
            const platformVersion = highEntropyValues.platformVersion;
            
            // Windows 11 tiene build number >= 22000
            // Windows 10 tiene build number < 22000
            // platformVersion viene como "10.0.22000" o similar
            if (platformVersion) {
              const versionParts = platformVersion.split('.');
              if (versionParts.length >= 3) {
                const buildNumber = parseInt(versionParts[2]);
                // Windows 11 tiene build >= 22000
                if (buildNumber >= 22000) {
                  return 'Windows 11';
                }
              } else if (versionParts.length === 2) {
                // Si solo tiene 2 partes, verificar la segunda
                const minorVersion = parseInt(versionParts[1]);
                // Windows 11 puede reportarse con versión mayor
                if (parseInt(versionParts[0]) >= 13 || minorVersion >= 22000) {
                  return 'Windows 11';
                }
              }
            }
          } catch (error) {
            // Si falla la API, usar método alternativo
          }
        }
        
        // Heurística: Windows 11 generalmente tiene Edge Chromium más reciente
        // y mejor soporte para ciertas APIs
        const isLikelyWindows11 = 
          navigator.userAgentData && 
          navigator.userAgentData.platform === 'Windows' &&
          'getHighEntropyValues' in navigator.userAgentData;
        
        if (isLikelyWindows11) {
          // Si tiene la API pero no pudimos obtener la versión, es probable que sea Windows 11
          return 'Windows 11';
        }
        
        return 'Windows 10';
      } else if (version === '6.3') {
        return 'Windows 8.1';
      } else if (version === '6.2') {
        return 'Windows 8';
      } else if (version === '6.1') {
        return 'Windows 7';
      } else if (version === '5.1' || version === '5.2') {
        return 'Windows XP';
      }
      return `Windows NT ${version}`;
    }
    
    // Fallback: Solo decir Windows sin versión
    return 'Windows';
  }
  
  // Detección de macOS con versión
  if (platform.includes('Mac') || userAgent.includes('Mac OS X') || userAgent.includes('Macintosh')) {
    const macVersion = userAgent.match(/Mac OS X (\d+[._]\d+)/);
    if (macVersion) {
      const version = macVersion[1].replace('_', '.');
      return `macOS ${version}`;
    }
    return 'macOS';
  }
  
  // Detección de Linux
  if (platform.includes('Linux')) {
    // Intentar detectar distribución específica con versión si está disponible
    if (userAgent.includes('Ubuntu')) {
      // Intentar extraer versión de Ubuntu si está en el userAgent
      // Nota: Los navegadores generalmente no incluyen la versión de distro en userAgent
      // Pero algunos pueden tener información como "Ubuntu/XX.XX"
      const ubuntuVersion = userAgent.match(/Ubuntu[/\s](\d+\.\d+)/i);
      if (ubuntuVersion) {
        return `Linux (Ubuntu ${ubuntuVersion[1]})`;
      }
      // Si no hay versión, intentar detectar por características del userAgent
      // Ubuntu 25.04 puede tener referencias específicas
      if (userAgent.match(/Ubuntu.*25[./]04/i)) {
        return 'Linux (Ubuntu 25.04)';
      }
      return 'Linux (Ubuntu)';
    }
    if (userAgent.includes('Fedora')) {
      const fedoraVersion = userAgent.match(/Fedora[/\s](\d+)/i);
      if (fedoraVersion) {
        return `Linux (Fedora ${fedoraVersion[1]})`;
      }
      return 'Linux (Fedora)';
    }
    if (userAgent.includes('Debian')) {
      const debianVersion = userAgent.match(/Debian[/\s](\d+)/i);
      if (debianVersion) {
        return `Linux (Debian ${debianVersion[1]})`;
      }
      return 'Linux (Debian)';
    }
    if (userAgent.includes('CentOS')) {
      const centosVersion = userAgent.match(/CentOS[/\s](\d+)/i);
      if (centosVersion) {
        return `Linux (CentOS ${centosVersion[1]})`;
      }
      return 'Linux (CentOS)';
    }
    // Intentar detectar otras distribuciones
    if (userAgent.includes('Arch')) return 'Linux (Arch)';
    if (userAgent.includes('SUSE')) return 'Linux (SUSE)';
    if (userAgent.includes('Red Hat') || userAgent.includes('RedHat')) return 'Linux (Red Hat)';
    return 'Linux';
  }
  
  // Detección de Android con versión
  if (platform.includes('Android') || userAgent.includes('Android')) {
    const androidVersion = userAgent.match(/Android (\d+(?:\.\d+)?)/);
    if (androidVersion) {
      return `Android ${androidVersion[1]}`;
    }
    return 'Android';
  }
  
  // Detección de iOS con versión
  if (platform.includes('iPhone') || platform.includes('iPad') || platform.includes('iPod') || 
      userAgent.includes('iPhone') || userAgent.includes('iPad') || userAgent.includes('iPod')) {
    const iosVersion = userAgent.match(/OS (\d+[._]\d+)/);
    if (iosVersion) {
      const version = iosVersion[1].replace('_', '.');
      return `iOS ${version}`;
    }
    return 'iOS';
  }
  
  // Si no se puede detectar, devolver el valor de platform o 'unknown'
  return platform || 'unknown';
};

/**
 * Hook personalizado para enviar heartbeat de actividad del usuario
 * @param {string} token - Token de autenticación del usuario
 */
export const useHeartbeat = (token) => {
  // Usar useRef para almacenar el sistema operativo detectado
  const operatingSystemRef = useRef('unknown');
  // Ubicación (GPS). Se actualiza de forma periódica y bajo demanda.
  const locationRef = useRef(null);
  // Información de red (Network Information API + RTT medido)
  const networkRef = useRef(null);
  
  useEffect(() => {
    // No enviar heartbeat si no hay token
    if (!token) return;

    // Obtener el ambiente
    const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'production';
    
    // Detectar sistema operativo de forma asíncrona al inicio
    detectOperatingSystem().then((os) => {
      operatingSystemRef.current = os;
    }).catch(() => {
      // Si falla la detección, mantener 'unknown'
      operatingSystemRef.current = 'unknown';
    });

    // Función para actualizar la ubicación usando la Geolocation API
    const updateLocation = () => {
      if (!('geolocation' in navigator)) {
        return;
      }
      try {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude, accuracy, altitudeAccuracy, speed, heading } = pos.coords || {};
            // Guardar únicamente si tenemos coordenadas válidas
            if (typeof latitude === 'number' && typeof longitude === 'number') {
              // Clasificar calidad de la geolocalización según precisión reportada (menor = mejor)
              // Nota: La Web Geolocation API no expone "potencia de señal GPS".
              // Usamos 'accuracy' (metros) como proxy y derivamos una categoría de calidad.
              let quality = 'unknown';
              if (typeof accuracy === 'number') {
                if (accuracy <= 10) quality = 'excellent';        // ~GPS de alta precisión
                else if (accuracy <= 30) quality = 'good';        // buena precisión
                else if (accuracy <= 100) quality = 'fair';       // usable
                else quality = 'poor';                            // señal/precisión baja
              }
              const positionTimestamp = typeof pos.timestamp === 'number' ? new Date(pos.timestamp).toISOString() : new Date().toISOString();
              locationRef.current = {
                latitude,
                longitude,
                accuracy,
                altitudeAccuracy,
                speed,
                heading,
                quality,                 // derivado de accuracy
                positionTimestamp,       // cuándo se obtuvo la lectura
                source: 'geolocation'    // origen de la medición expuesta por el navegador
              };
            }
          },
          // En caso de error/denegación, simplemente no incluimos ubicación
          () => {
            // mantener último valor válido o null
          },
          {
            enableHighAccuracy: true,
            timeout: 7000,      // evitar bloqueos si el GPS tarda
            maximumAge: 60_000, // reutilizar lectura de hasta 60s si está disponible
          }
        );
      } catch (e) {
        // Silencioso: no bloquear el heartbeat por fallos de geolocalización
      }
    };

    // Intento inicial de geolocalización (pedirá permiso al usuario)
    updateLocation();
    // Refrescar la ubicación cada 5 minutos para no forzar GPS en cada heartbeat
    const geoInterval = setInterval(updateLocation, 5 * 60 * 1000);

    // --- Información de red (calidad de enlace) ---
    const readConnection = () => {
      const navConn = navigator.connection || navigator.webkitConnection || navigator.mozConnection;
      if (!navConn) return null;
      const { effectiveType, downlink, rtt, saveData } = navConn;
      return {
        effectiveType,                  // 'slow-2g' | '2g' | '3g' | '4g' (no 5G, se reporta como '4g' en muchos navegadores)
        downlinkMbps: typeof downlink === 'number' ? downlink : undefined,
        rttMs: typeof rtt === 'number' ? rtt : undefined,
        saveData: !!saveData,
      };
    };

    const classifyNetwork = (info) => {
      if (!info) return 'unknown';
      const rtt = info.measuredRttMs ?? info.rttMs;
      const down = info.downlinkMbps ?? 0;
      const eff = info.effectiveType || '';
      if (rtt != null && rtt <= 100 && down >= 10) return 'excellent';
      if ((rtt != null && rtt <= 200 && down >= 5) || eff === '4g') return 'good';
      if ((rtt != null && rtt <= 600 && down >= 1) || eff === '3g') return 'fair';
      return 'poor';
    };

    const measureRtt = async () => {
      // Medición ligera de RTT; intentamos un endpoint de ping y caemos a una petición simple cross-origin
      const start = performance.now();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      try {
        // Primer intento: endpoint de ping del backend (si existe)
        const pingUrl = 'https://makana.julianvalencia.shop/api/ping';
        await fetch(pingUrl, {
          method: 'GET',
          cache: 'no-store',
          credentials: 'omit',
          signal: controller.signal,
        }).catch(() => {
          // Fallback a petición muy ligera (opaque), aún así nos sirve para medir tiempo de ida y vuelta.
          return fetch('https://makana.julianvalencia.shop/', {
            method: 'GET',
            mode: 'no-cors',
            cache: 'no-store',
            signal: controller.signal,
          });
        });
        const end = performance.now();
        clearTimeout(timeoutId);
        return Math.round(end - start);
      } catch {
        clearTimeout(timeoutId);
        return undefined;
      }
    };

    const updateNetworkInfo = async () => {
      const base = readConnection();
      const measuredRttMs = await measureRtt();
      const snapshot = {
        ...(base || {}),
        measuredRttMs,
        timestamp: new Date().toISOString(),
      };
      snapshot.quality = classifyNetwork(snapshot);
      networkRef.current = snapshot;
    };

    // Inicial y actualización periódica de la info de red
    updateNetworkInfo();
    const netInterval = setInterval(updateNetworkInfo, 3 * 60 * 1000); // cada 3 minutos
    const navConn = navigator.connection || navigator.webkitConnection || navigator.mozConnection;
    const onConnChange = () => updateNetworkInfo();
    if (navConn && typeof navConn.addEventListener === 'function') {
      try { navConn.addEventListener('change', onConnChange); } catch { /* noop */ }
    } else if (navConn && 'onchange' in navConn) {
      // fallback antiguo
      try { navConn.onchange = onConnChange; } catch { /* noop */ }
    }

    const sendHeartbeat = async () => {
      // Si aún no se ha detectado el OS, intentar detectarlo ahora
      if (operatingSystemRef.current === 'unknown') {
        try {
          operatingSystemRef.current = await detectOperatingSystem();
        } catch (error) {
          // Si falla, mantener 'unknown'
          operatingSystemRef.current = 'unknown';
        }
      }
      
      fetch('https://makana.julianvalencia.shop/api/activity/heartbeat', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-App-Environment': ENVIRONMENT  // Header para separar entornos
        },
        body: JSON.stringify({
          currentPage: window.location.pathname,
          activeTab: document.title,
          timestamp: new Date().toISOString(),
          operatingSystem: operatingSystemRef.current,  // Identificador del sistema operativo
          // Adjuntar ubicación solo si está disponible (y usuario otorgó permiso)
          location: locationRef.current,
          // Adjuntar snapshot de red (si disponible)
          network: networkRef.current
        })
      }).catch((error) => {
        // Log silencioso usando feature flags
        debugLog('💓 Heartbeat error (silent):', error.message);
      });
    };

    // Enviar primer heartbeat inmediatamente al cargar (con detección de OS)
    sendHeartbeat();

    // Programar heartbeat cada 1.5 minutos (90000 ms)
    const heartbeatInterval = setInterval(sendHeartbeat, 90000);

    // Limpiar intervalo al desmontar el componente o cambiar token
    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(geoInterval);
      clearInterval(netInterval);
      const navConnLocal = navigator.connection || navigator.webkitConnection || navigator.mozConnection;
      if (navConnLocal && typeof navConnLocal.removeEventListener === 'function') {
        try { navConnLocal.removeEventListener('change', onConnChange); } catch { /* noop */ }
      } else if (navConnLocal && 'onchange' in navConnLocal) {
        try { navConnLocal.onchange = null; } catch { /* noop */ }
      }
    };
  }, [token]); // Dependencia: solo re-ejecutar si el token cambia
};
